import { ApolloError } from '@apollo/client';
import { AreaLoader, setAnnouncement } from '@starrez/starryui';
import { color, isEscape, Text } from '@starrez/starryui9';
import { useSentry } from '@starrez/starryutils';
import React from 'react';
import { ErrorDisplay } from '../../../../../../.starryUI/Errors/ErrorDisplay';
import { IMainDirectoryListItem } from '../../../graphql/useAllMainDirectoryListQuery';
import { strings } from '../../../localization';
import { useViewPicker } from '../context/ViewPickerContext';
import { ViewItem } from './ViewItem';
import { ItemContainer } from './ViewItem.styles';

declare let SEND_TO_SENTRY: boolean;

interface IViewOptionsProps {
    loading: boolean;
    error?: ApolloError | undefined;
    searchString: string;
    searchResults: IMainDirectoryListItem[] | undefined;
}

export const ViewOptions = ({ loading, error, searchString, searchResults }: IViewOptionsProps) => {
    const { closePicker, favouriteViews } = useViewPicker();
    const { sayPolitely } = setAnnouncement();
    const { captureException } = useSentry();

    if (loading)
        return (
            <ItemContainer>
                <AreaLoader />
            </ItemContainer>
        );
    if (error) {
        if (SEND_TO_SENTRY) {
            captureException(error);
        }

        return (
            <ItemContainer>
                <ErrorDisplay error={error} />
            </ItemContainer>
        );
    }

    const showNoResults = Boolean(searchString.length && !searchResults?.length);
    let views = favouriteViews?.filter((view) => view.favourite);

    if (searchString.length) {
        views = searchResults?.length ? searchResults : [];

        if (showNoResults) {
            sayPolitely(strings.formatString(strings.ViewSearch_Label_NoSearchResultsFound, searchString) as string);
        } else {
            sayPolitely(`${searchResults?.length || 0} found.`);
        }
    }

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLLIElement>) => {
        if (isEscape(e)) {
            e.stopPropagation();
            closePicker();
        }
    };

    return (
        <>
            {showNoResults && (
                <ItemContainer tabIndex={0} onKeyDown={(e) => handleOnKeyDown(e)}>
                    <Text variant="normal.bold" color={color.Grey.G60}>
                        {strings.ViewSearch_Label_NoResultsFound}
                    </Text>
                </ItemContainer>
            )}
            {views?.map((view) => (
                <ViewItem key={`view-item-${view.id}`} {...view} searchString={searchString} closeViewPicker showViewTypeIcon={true} />
            ))}
        </>
    );
};
